import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Database, Sparkles, CheckCircle2, AlertTriangle, Loader2, PlayCircle, Info, BookOpen, Layers, Wrench
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/* -------------------------------------------------------------------------- */
/* Result display                                                             */
/* -------------------------------------------------------------------------- */
const ResultBlock = ({ result, error, testid }) => {
  if (!result && !error) return null;
  if (error) {
    return (
      <div className="mt-4 p-4 rounded-xl border border-rose-300 bg-rose-50" data-testid={testid + '-error'}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-rose-900">Fehler</div>
            <div className="text-sm text-rose-800 mt-1">{error}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-4 p-4 rounded-xl border border-emerald-300 bg-emerald-50" data-testid={testid + '-success'}>
      <div className="flex items-start gap-2 mb-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div className="font-semibold text-emerald-900">Erfolgreich ausgeführt</div>
      </div>
      <pre className="text-[11.5px] leading-relaxed text-espresso/80 bg-cream-100/70 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Action Card                                                                */
/* -------------------------------------------------------------------------- */
const ActionCard = ({
  icon: Icon, title, subtitle, description, buttonLabel, running, onClick,
  result, error, testid, badge,
}) => (
  <div className="bg-cream-100 rounded-2xl border border-cream-300 p-6" data-testid={testid + '-card'}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-terra/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-terra" />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-terra">{subtitle}</div>
          <h2 className="font-display text-2xl text-sand-800 leading-tight">{title}</h2>
        </div>
      </div>
      {badge && (
        <span className="text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold">
          {badge}
        </span>
      )}
    </div>

    <p className="text-sm text-espresso/80 leading-relaxed mb-5">
      {description}
    </p>

    <button
      onClick={onClick}
      disabled={running}
      data-testid={testid + '-btn'}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-espresso text-cream-50
                 hover:bg-espresso/90 disabled:opacity-50 disabled:cursor-not-allowed
                 transition font-medium text-sm"
    >
      {running ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Läuft…
        </>
      ) : (
        <>
          <PlayCircle className="w-4 h-4" />
          {buttonLabel}
        </>
      )}
    </button>

    <ResultBlock result={result} error={error} testid={testid} />
  </div>
);

/* -------------------------------------------------------------------------- */
/* Main Page                                                                  */
/* -------------------------------------------------------------------------- */
const AdminSystemMigrationPage = ({ Layout }) => {
  const { getAuthHeader } = useAuth();

  const [dedupRunning, setDedupRunning] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);
  const [dedupError, setDedupError] = useState(null);

  const [seedRunning, setSeedRunning] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedError, setSeedError] = useState(null);
  const [seedOverwrite, setSeedOverwrite] = useState(false);

  const [courseSeedRunning, setCourseSeedRunning] = useState(false);
  const [courseSeedResult, setCourseSeedResult] = useState(null);
  const [courseSeedError, setCourseSeedError] = useState(null);

  const [expandRunning, setExpandRunning] = useState(false);
  const [expandResult, setExpandResult] = useState(null);
  const [expandError, setExpandError] = useState(null);

  const [exportRunning, setExportRunning] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportError, setExportError] = useState(null);

  const [repairRunning, setRepairRunning] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [repairError, setRepairError] = useState(null);

  const runRepair = async () => {
    setRepairRunning(true);
    setRepairError(null);
    setRepairResult(null);
    try {
      const combined = {};
      // Schritt 1: verwaiste Kurs-Zuweisungen
      const preview = await axios.post(`${API}/admin/repair/orphan-assignments?dry_run=true`, {}, getAuthHeader());
      const p = preview.data;
      if (p.repairable > 0) {
        const lines = (p.details || [])
          .filter((d) => d.new_course_title)
          .slice(0, 12)
          .map((d) => `• ${d.user}: „${d.old_course_title}" → „${d.new_course_title}" (${d.matched_modules} Module mit Lerndaten)`)
          .join('\n');
        if (window.confirm(`${p.repairable} verwaiste Kurs-Zuweisung(en) gefunden:\n\n${lines}\n\nJetzt reparieren?`)) {
          const res = await axios.post(`${API}/admin/repair/orphan-assignments?dry_run=false`, {}, getAuthHeader());
          combined.zuweisungen = res.data;
        } else {
          combined.zuweisungen = p;
        }
      } else {
        combined.zuweisungen = { meldung: 'Keine verwaisten Zuweisungen.' };
      }
      // Schritt 2: Fortschrittsdaten normalisieren (course_id aus module_id ableiten)
      const nprev = await axios.post(`${API}/admin/repair/normalize-progress?dry_run=true`, {}, getAuthHeader());
      const n = nprev.data;
      const fixable = Object.values(n.stats || {}).reduce((s, c) => s + (c.korrigierbar || 0), 0);
      if (fixable > 0) {
        if (window.confirm(
          `${fixable} Fortschritts-Einträge zeigen auf eine veraltete Kurs-ID und werden dem richtigen Kurs zugeordnet ` +
          `(Gelesen-Status, Lernzeit, Quiz-Ergebnisse, Aufgaben).\n\nJetzt korrigieren? Es wird nichts gelöscht.`
        )) {
          const nres = await axios.post(`${API}/admin/repair/normalize-progress?dry_run=false`, {}, getAuthHeader());
          combined.fortschrittsdaten = nres.data;
          toast.success('Fortschrittsdaten korrigiert');
        } else {
          combined.fortschrittsdaten = n;
        }
      } else {
        combined.fortschrittsdaten = { meldung: 'Alle Fortschrittsdaten korrekt zugeordnet.', ...n };
      }
      setRepairResult(combined);
    } catch (e) {
      setRepairError(e.response?.data?.detail || e.message);
      toast.error('Reparatur fehlgeschlagen');
    }
    setRepairRunning(false);
  };

  const runExportSeed = async () => {
    if (!window.confirm(
      'Seed-Datei neu exportieren?\n\n' +
      'Schreibt ALLE aktuellen LLM-Modulinhalte + Claude-Quizfragen aus dieser ' +
      'Datenbank in die Seed-Datei. Nur auf der PREVIEW sinnvoll — danach Redeploy, ' +
      'dann auf Produktion „LLM-Inhalte & Quizfragen übertragen" ausführen.'
    )) return;
    setExportRunning(true);
    setExportError(null);
    setExportResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/enhance/export-seed?confirm=true`,
        {},
        getAuthHeader()
      );
      setExportResult(res.data);
      toast.success(`Export fertig: ${res.data.documents} Inhalte, ${res.data.quiz_questions} Quizfragen`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setExportError(msg);
      toast.error('Export fehlgeschlagen');
    }
    setExportRunning(false);
  };

  const runExpand = async () => {
    if (!window.confirm(
      'Erweiterungs-Module anlegen?\n\n' +
      'Fügt die kuratierten Zusatz-Module (z. B. Wundmanagement Leitung Modul 9–22, ' +
      'Behandlungspflege Modul 17–35) zu 14 Kursen hinzu, damit die Modul-Anzahl ' +
      'überall mit den vorgenerierten LLM-Inhalten übereinstimmt.\n\n' +
      'Die Aktion ist idempotent — vorhandene Module werden übersprungen, ' +
      'Nutzerfortschritt bleibt erhalten.'
    )) return;
    setExpandRunning(true);
    setExpandError(null);
    setExpandResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/migration/expand-courses?confirm=true`,
        {},
        getAuthHeader()
      );
      setExpandResult(res.data);
      toast.success(`Erweiterung fertig: ${res.data.total_added} Module angelegt`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setExpandError(msg);
      toast.error('Erweiterung fehlgeschlagen');
    }
    setExpandRunning(false);
  };

  const runCourseSeed = async () => {
    if (!window.confirm(
      'Kurse & Module aus den Kursdefinitionen in die Datenbank übertragen?\n\n' +
      'Neue Kurse (z. B. Expertenkurs Außerklinische Intensivpflege) werden angelegt, ' +
      'bestehende Kurse werden aktualisiert. Nutzerdaten (Fortschritt, Zuweisungen) bleiben erhalten.\n\n' +
      'Die Aktion ist idempotent.'
    )) return;
    setCourseSeedRunning(true);
    setCourseSeedError(null);
    setCourseSeedResult(null);
    try {
      const res = await axios.post(`${API}/seed`, {}, getAuthHeader());
      setCourseSeedResult(res.data);
      toast.success('Kurs-Seed erfolgreich ausgeführt');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setCourseSeedError(msg);
      toast.error('Kurs-Seed fehlgeschlagen');
    }
    setCourseSeedRunning(false);
  };

  const runDedup = async () => {
    if (!window.confirm(
      'Task-Deduplizierung anwenden?\n\n' +
      'Diese Aktion kopiert bereits abgegebene Antworten von alten Task-IDs auf die neuen ' +
      'Task-IDs, damit kein Nutzerfortschritt verloren geht.\n\n' +
      'Die Aktion ist idempotent — sie kann problemlos mehrmals ausgeführt werden.'
    )) return;
    setDedupRunning(true);
    setDedupError(null);
    setDedupResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/dedup-migration/apply?confirm=true`,
        {},
        getAuthHeader()
      );
      setDedupResult(res.data);
      toast.success(`Deduplizierung fertig: ${res.data.created} neue Submissions angelegt`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setDedupError(msg);
      toast.error('Deduplizierung fehlgeschlagen');
    }
    setDedupRunning(false);
  };

  const [userSeedRunning, setUserSeedRunning] = useState(false);
  const [userSeedResult, setUserSeedResult] = useState(null);
  const [userSeedError, setUserSeedError] = useState(null);

  const runUserSeed = async () => {
    if (!window.confirm(
      'Vorbereitete Teilnehmer anlegen?\n\n' +
      'Alle Nutzer aus der Nutzer-Seed-Datei werden mit ihren Kurszuweisungen angelegt. ' +
      'Bereits vorhandene Benutzernamen werden übersprungen. Es werden KEINE E-Mails versendet.\n\n' +
      'Die Aktion ist idempotent.'
    )) return;
    setUserSeedRunning(true);
    setUserSeedError(null);
    setUserSeedResult(null);
    try {
      const res = await axios.post(`${API}/admin/enhance/users-seed-from-file?confirm=true`, {}, getAuthHeader());
      setUserSeedResult(res.data);
      toast.success(`Nutzer-Import fertig: ${res.data.created.length} neu, ${res.data.skipped_existing.length} übersprungen, ${res.data.new_assignments} Kurszuweisungen`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setUserSeedError(msg);
      toast.error('Nutzer-Import fehlgeschlagen');
    }
    setUserSeedRunning(false);
  };

  const runSeed = async () => {
    if (!window.confirm(
      'LLM-Inhalte auf diese Datenbank übertragen?\n\n' +
      'Alle vorgenerierten LLM-Modulinhalte + LLM-Quizfragen aus der Seed-Datei werden importiert.\n\n' +
      (seedOverwrite
        ? 'ÜBERSCHREIBEN AKTIV: Bestehende Modulinhalte werden durch die neuen Versionen ersetzt (z. B. Claude-Upgrade).'
        : 'Bereits vorhandene Modulinhalte werden ÜBERSPRUNGEN (kein Überschreiben).') +
      '\n\nDie Aktion ist idempotent.'
    )) return;
    setSeedRunning(true);
    setSeedError(null);
    setSeedResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/enhance/seed-from-file?confirm=true&overwrite=${seedOverwrite}`,
        {},
        getAuthHeader()
      );
      setSeedResult(res.data);
      toast.success(`Seed fertig: ${res.data.inserted} neu, ${res.data.updated} aktualisiert, ${res.data.quiz_questions_imported} Quizfragen`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      setSeedError(msg);
      toast.error('Seed fehlgeschlagen');
    }
    setSeedRunning(false);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto animate-fade-in" data-testid="admin-system-migration-page">
        {/* Header */}
        <div className="mb-8">
          <div className="text-[11.5px] uppercase tracking-[0.28em] font-medium text-terra mb-2">
            Admin · System
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-sand-800 tracking-tight leading-none">
            System-Migration
          </h1>
          <p className="text-sm text-espresso/70 mt-3 max-w-2xl">
            Einmalige Setup-Aktionen nach einem Deployment. Ausschließlich nach Absprache oder
            nach Roll-out neuer Inhalte auszuführen. Alle Aktionen sind idempotent — mehrfache
            Ausführung schadet nicht.
          </p>
        </div>

        {/* Info Banner */}
        <div className="mb-8 p-4 rounded-xl bg-amber-50 border border-amber-300 flex items-start gap-3" data-testid="migration-info-banner">
          <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <span className="font-semibold">Hinweis: </span>
            Diese Aktionen ändern die <strong>aktuelle</strong> Datenbank (Preview oder Produktion —
            je nachdem, wo du eingeloggt bist). Nach jedem Deployment mit neuen Inhalten die
            Buttons einmal auf der Produktion klicken.
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 gap-6">
          <ActionCard
            icon={Database}
            subtitle="Schritt 0 · Nur Preview"
            title="Seed-Datei exportieren"
            description={
              "Schreibt alle aktuellen LLM-Modulinhalte und Claude-Quizfragen aus dieser Datenbank " +
              "in die Seed-Datei (data/enhanced_content_seed.json). Auf der Preview ausführen, " +
              "danach Redeploy — erst dann kann die Produktion die neuen Inhalte über Schritt 3 importieren."
            }
            buttonLabel="Seed-Datei exportieren"
            running={exportRunning}
            onClick={runExportSeed}
            result={exportResult}
            error={exportError}
            testid="export-seed-migration"
          />

          <ActionCard
            icon={Wrench}
            subtitle="Reparatur"
            title="Verschwundenen Fortschritt reparieren"
            description={
              "Findet Teilnehmer, deren Kurs-Zuweisung nach einem Kurs-Update auf eine alte Kurs-ID " +
              "zeigt (Symptom: Fortschritt und Module stehen plötzlich auf 0, obwohl Lernzeit da ist). " +
              "Die Zuweisung wird anhand der echten Lerndaten auf den richtigen Kurs umgehängt — " +
              "es wird zuerst eine Vorschau angezeigt, nichts passiert ohne Bestätigung."
            }
            buttonLabel="Prüfen & reparieren"
            running={repairRunning}
            onClick={runRepair}
            result={repairResult}
            error={repairError}
            testid="repair-orphans"
          />

          <ActionCard
            icon={BookOpen}
            subtitle="Schritt 1"
            title="Kurse & Module seeden"
            description={
              "Überträgt alle Kursdefinitionen (inkl. neuer Kurse wie dem Expertenkurs " +
              "Außerklinische Intensivpflege) aus dem Code in die Datenbank. Bestehende Kurse " +
              "werden aktualisiert, Nutzerdaten bleiben unangetastet. Nach jedem Deployment mit " +
              "neuen Kursen einmal ausführen."
            }
            buttonLabel="Kurse seeden"
            running={courseSeedRunning}
            onClick={runCourseSeed}
            result={courseSeedResult}
            error={courseSeedError}
            testid="course-seed-migration"
          />

          <ActionCard
            icon={Layers}
            subtitle="Schritt 1b"
            title="Erweiterungs-Module anlegen"
            description={
              "Legt die kuratierten Zusatz-Module für 14 Kurse an (z. B. Wundmanagement Leitung " +
              "Modul 9–22, Behandlungspflege Modul 17–35). Nötig, damit die Modul-Anzahl überall " +
              "stimmt und die vorgenerierten LLM-Inhalte dieser Module sichtbar werden. " +
              "Insert-only und idempotent — Fortschritt und bestehende Module bleiben unangetastet."
            }
            buttonLabel="Erweiterungs-Module anlegen"
            running={expandRunning}
            onClick={runExpand}
            result={expandResult}
            error={expandError}
            testid="expand-courses-migration"
          />

          <ActionCard
            icon={Database}
            subtitle="Schritt 2"
            title="Datenbank-Deduplizierung anwenden"
            description={
              "Kopiert Nutzer-Antworten von alten (dedup) Task-IDs auf die neuen -b/-c-Task-IDs. " +
              "Verhindert, dass Kursteilnehmende ihren Fortschritt bei betroffenen Aufgaben verlieren. " +
              "Ändert niemals bestehende Antworten und legt nur fehlende Kopien an."
            }
            buttonLabel="Deduplizierung ausführen"
            running={dedupRunning}
            onClick={runDedup}
            result={dedupResult}
            error={dedupError}
            testid="dedup-migration"
          />

          <ActionCard
            icon={Sparkles}
            subtitle="Schritt 3"
            title="LLM-Inhalte & Quizfragen übertragen"
            description={
              "Importiert alle vorgenerierten LLM-Modulinhalte und LLM-Prüfungsfragen aus der " +
              "Seed-Datei in die Datenbank. Ohne Überschreiben werden vorhandene Einträge übersprungen. " +
              "Mit Überschreiben werden alte Modulversionen durch die neuen ersetzt (nötig nach " +
              "Modell-Upgrades, z. B. GPT-4o → Claude)."
            }
            buttonLabel="Inhalte übertragen"
            running={seedRunning}
            onClick={runSeed}
            result={seedResult}
            error={seedError}
            testid="seed-migration"
          />

          <label className="flex items-center gap-3 -mt-2 ml-2 cursor-pointer select-none" data-testid="seed-overwrite-toggle">
            <input
              type="checkbox"
              checked={seedOverwrite}
              onChange={(e) => setSeedOverwrite(e.target.checked)}
              className="w-4 h-4 accent-terra"
              data-testid="seed-overwrite-checkbox"
            />
            <span className="text-sm text-espresso/80">
              Bestehende Modulinhalte <strong>überschreiben</strong> (bei Modell-Upgrades aktivieren)
            </span>
          </label>

          <ActionCard
            icon={Sparkles}
            subtitle="Schritt 4"
            title="Vorbereitete Teilnehmer anlegen"
            description={
              "Legt alle in der Nutzer-Seed-Datei vorbereiteten Teilnehmer samt Kurszuweisungen an " +
              "(z. B. Kleeblüte-Fortbildungen). Bereits vorhandene Benutzernamen werden übersprungen, " +
              "es werden keine E-Mails versendet."
            }
            buttonLabel="Teilnehmer anlegen"
            running={userSeedRunning}
            onClick={runUserSeed}
            result={userSeedResult}
            error={userSeedError}
            testid="users-seed-migration"
          />
        </div>

        {/* Footer note */}
        <div className="mt-10 text-xs text-espresso/50 leading-relaxed max-w-3xl">
          Endpoints: <code className="bg-cream-200 px-1.5 py-0.5 rounded">POST /api/admin/dedup-migration/apply</code>
          {' · '}
          <code className="bg-cream-200 px-1.5 py-0.5 rounded">POST /api/admin/enhance/seed-from-file</code>
        </div>
      </div>
    </Layout>
  );
};

export default AdminSystemMigrationPage;
